import { InputMask } from '@components/atoms';
import { UserData, useUsers } from '@hooks';
import { Form, Input, Modal, Select } from 'antd';
import { useEffect } from 'react';

interface UserFormModalProps {
  open: boolean;
  onCancel: () => void;
  editingUser: UserData | null;
}

export const UserFormModal = ({ open, onCancel, editingUser }: UserFormModalProps) => {
  const [form] = Form.useForm();
  const { createUser, updateUser } = useUsers();
  const isEditing = !!editingUser;

  // Fill the form when editing a user
  useEffect(() => {
    if (editingUser) {
      form.setFieldsValue({
        email: editingUser.email,
        profile: editingUser.profile,
        tier: editingUser.tier || 'retail',
        physicalPerson: {
          name: editingUser.physicalPerson?.name || '',
          phone: editingUser.physicalPerson?.phone || '',
        },
      });
    } else {
      form.resetFields();
    }
  }, [editingUser, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (isEditing && editingUser) {
        await updateUser.mutateAsync({
          id: editingUser.id,
          data: values,
        });
      } else {
        await createUser.mutateAsync(values);
      }

      form.resetFields();
      onCancel();
    } catch (error) {
      console.error('Form error:', error);
    }
  };

  return (
    <Modal
      title={
        <span className="font-displayed !font-normal text-2xl text-orangicide">
          {isEditing ? 'EDIT USER' : 'ADD USER'}
        </span>
      }
      open={open}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={createUser.isPending || updateUser.isPending}
      okText={isEditing ? 'Update' : 'Add'}
      cancelText="Cancel"
      okButtonProps={{
        className: 'font-displayed !font-normal !text-xl text-black btn-orangicide'
      }}
      cancelButtonProps={{
        className: 'font-displayed !font-normal !text-xl text-black btn-orangicide'
      }}
      className="[&_.ant-modal-content]:!bg-zinc-800 [&_.ant-modal-header]:!bg-zinc-800"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          profile: 'user',
          tier: 'retail',
          physicalPerson: {
            name: '',
            phone: '',
            ssn: '',
          },
        }}
      >
        <Form.Item
          name="email"
          label={<span className="!text-white">Email</span>}
          rules={[
            { required: true, message: 'please enter an email' },
            { type: 'email', message: 'please enter a valid email' },
          ]}
        >
          <Input
            placeholder="user email"
            className="!text-white"
          />
        </Form.Item>

        <Form.Item
          name="profile"
          label={<span className="!text-white">Profile</span>}
          rules={[{ required: true, message: 'please select a profile' }]}
        >
          <Select
            placeholder="select profile"
            className="!font-orator [&_.ant-select-selection-item]:!text-zinc-400"
          >
            <Select.Option value="admin">Administrator</Select.Option>
            <Select.Option value="manager">Manager</Select.Option>
            <Select.Option value="user">User</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="tier"
          label={<span className="!text-white">Tier</span>}
          rules={[{ required: true, message: 'please select a tier' }]}
        >
          <Select
            placeholder="select tier"
            className="!font-orator [&_.ant-select-selection-item]:!text-zinc-400"
          >
            <Select.Option value="tier1">Tier 1 (12% off)</Select.Option>
            <Select.Option value="tier2">Tier 2 (9% off)</Select.Option>
            <Select.Option value="tier3">Tier 3 (5% off)</Select.Option>
            <Select.Option value="retail">Retail</Select.Option>
          </Select>
        </Form.Item>

        <div className="!mt-12 !mb-2">
          <h4 className="!font-orator !font-normal text-orangicide text-lg tracking-wider">Personal Information</h4>
        </div>

        <Form.Item
          label={<span className="!text-white">Name</span>}
          name={['physicalPerson', 'name']}
          rules={[{ required: true, message: 'please enter a name' }]}
        >
          <Input
            placeholder="full name"
            className="!text-white"
          />
        </Form.Item>

        <Form.Item
          label={<span className="!text-white">Phone</span>}
          name={['physicalPerson', 'phone']}
        >
          <InputMask
            mask="usphone"
            placeholder="(000) 000-0000"
            className="!text-white"
          />
        </Form.Item>
        <div className="mb-8"></div>
      </Form>
    </Modal>
  );
};
